import { Client as ColyseusClient } from 'colyseus.js';
import { isDuplicateSessionKick } from '../contracts/wsClose';
import { getColyseusWsUrl } from './colyseusUrl';
import {
  LOBBY_CLOSE_CONSENTED,
  readPersistedLobbyReconnect,
  writePersistedLobbyReconnect,
  clearPersistedLobbyReconnect,
} from './lobbyReconnectUtils';

const COMMANDS = {
  LIST_TABLES: 'lobby/list-tables',
  CREATE_TABLE: 'lobby/create-table',
  JOIN_TABLE: 'lobby/join-table',
  LEAVE_TABLE: 'lobby/leave-table',
  SET_READY: 'lobby/set-ready',
  KICK_FROM_TABLE: 'lobby/kick-from-table',
  TABLE_WAITING_CHAT: 'lobby/table-waiting-chat',
  FRIEND_REQUEST_NOTIFY: 'lobby/friend-request-notify',
  FRIEND_REQUEST_RESOLVE: 'lobby/friend-request-resolve',
  GLOBAL_LOBBY_CHAT: 'lobby/global-lobby-chat',
  FRIEND_DM_SEND: 'lobby/friend-dm-send',
  FRIEND_DM_SYNC: 'lobby/friend-dm-sync',
  FRIEND_DM_MARK_READ: 'lobby/friend-dm-mark-read',
  SEND_INVITE: 'social/send-invite',
  RESOLVE_INVITE: 'social/resolve-invite',
};

const EVENTS = {
  ERROR: 'system/error',
  ACK: 'system/ack',
  LOBBY_STATE: 'lobby/state',
  TABLE_CREATED: 'lobby/table-created',
  TABLE_UPDATED: 'lobby/table-updated',
  TABLE_REMOVED: 'lobby/table-removed',
  TABLE_WAITING_CHAT: 'lobby/table-waiting-chat-msg',
  LOBBY_FRIEND_REQUEST: 'lobby/friend-request',
  LOBBY_FRIEND_REQUEST_RESOLVED: 'lobby/friend-request-resolved',
  LOBBY_GLOBAL_LOBBY_CHAT: 'lobby/global-lobby-chat-msg',
  LOBBY_FRIEND_DM_MESSAGE: 'lobby/friend-dm-msg',
  LOBBY_FRIEND_DM_SYNC: 'lobby/friend-dm-sync',
  SOCIAL_INVITE: 'social/invite',
};
const DEFAULT_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const DEFAULT_BELOT_VARIANT = 'armenian';

function createEnvelope(command, data) {
  return {
    command,
    meta: {
      commandId: `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      clientVersion: `web-${process.env.REACT_APP_VERSION || 'dev'}`,
    },
    data: data ?? {},
  };
}

function toLegacyTable(table) {
  const id = String(table?.tableId || table?.id || '')
    .trim()
    .toUpperCase();
  const profiles =
    table?.playerProfiles && typeof table.playerProfiles === 'object' ? table.playerProfiles : {};
  const order = table?.playerOrder || [];
  const players = Array.isArray(table?.players)
    ? table.players
    : order.map((userId) => {
        const uid = String(userId);
        const p = profiles[uid] || {};
      const avatarUrl = String(p.avatarUrl || '').trim() || DEFAULT_GUEST_AVATAR_URL;
        return {
          id: uid,
          userId: uid,
          username: String(p.username || uid),
          level: Math.max(1, Math.floor(Number(p.level) || 1)),
        avatarUrl,
        };
      });
  return {
    id,
    gameRoomId: String(table?.gameRoomId || ''),
    mode: String(table?.mode || 'seka'),
    belotVariant:
      String(table?.mode || 'seka') === 'belot'
        ? String(table?.belotVariant || '').trim().toLowerCase() || DEFAULT_BELOT_VARIANT
        : '',
    belotTargetScore:
      String(table?.mode || 'seka') === 'belot'
        ? Number(table?.belotTargetScore) === 501
          ? 501
          : 1001
        : undefined,
    name: String(table?.name || `Стол #${id}`),
    currency: String(table?.currency || 'seka'),
    buyIn: Number(table?.buyIn || 0),
    maxPlayers: Number(table?.maxPlayers || 4),
    status: String(table?.status || 'waiting'),
    players,
    hostUserId: String(table?.hostUserId || ''),
    readyUserIds: Array.isArray(table?.readyUserIds) ? [...table.readyUserIds] : [],
    playerProfiles:
      table?.playerProfiles && typeof table.playerProfiles === 'object' ? { ...table.playerProfiles } : {},
  };
}

class ColyseusLobbyTransport {
  /**
   * @param {import('colyseus.js').Room} room
   * @param {import('colyseus.js').Client} colyseusClient
   * @param {{ user: object, accessToken?: string, mode: string }} joinArgs
   */
  constructor(room, colyseusClient, joinArgs) {
    this.room = room;
    this.client = colyseusClient;
    this.joinArgs = joinArgs;
    this.connected = true;
    this.listeners = new Map();
    this._intentionalDisconnect = false;
    this._reconnectAttempt = 0;
    this._leaveHandler = (code, reason) => {
      void this._handleRoomLeave(code, reason);
    };
    this.room.onLeave(this._leaveHandler);
    this._wireInbound();
    writePersistedLobbyReconnect(joinArgs.mode, room);
  }

  /**
   * @param {object} user
   * @param {string} [accessToken] — JWT того же секрета, что `SEKA_JWT_SECRET` на Colyseus (как REST :3333)
   * @param {string} [mode] — `filterBy` лобби на сервере
   */
  static async connect(user, accessToken, mode = 'seka') {
    const client = new ColyseusClient(getColyseusWsUrl());
    if (accessToken) {
      client.http.authToken = String(accessToken);
    }
    const joinArgs = {
      user,
      accessToken,
      mode: String(mode || 'seka'),
    };
    const joinOpts = {
      mode: joinArgs.mode,
      userId: String(user?.id || 'guest'),
      username: String(user?.username || 'Игрок'),
      clan: String(user?.clan || ''),
      level: Math.max(1, Math.floor(Number(user?.level) || 1)),
      avatarUrl: String(user?.avatarUrl || DEFAULT_GUEST_AVATAR_URL).trim().slice(0, 12000),
    };

    const persisted = readPersistedLobbyReconnect(joinArgs.mode);
    let room = null;
    if (persisted) {
      try {
        room = await client.reconnect(persisted);
      } catch {
        room = null;
        clearPersistedLobbyReconnect(joinArgs.mode);
      }
    }
    if (!room) {
      room = await client.joinOrCreate('lobby', joinOpts);
    }
    return new ColyseusLobbyTransport(room, client, joinArgs);
  }

  _joinOptsForFallback() {
    const u = this.joinArgs.user;
    return {
      mode: this.joinArgs.mode,
      userId: String(u?.id || 'guest'),
      username: String(u?.username || 'Игрок'),
      clan: String(u?.clan || ''),
      level: Math.max(1, Math.floor(Number(u?.level) || 1)),
      avatarUrl: String(u?.avatarUrl || DEFAULT_GUEST_AVATAR_URL).trim().slice(0, 12000),
    };
  }

  async _handleRoomLeave(code, reason) {
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* room destroyed */
    }

    const token = this.room?.reconnectionToken;

    if (this._intentionalDisconnect) {
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, intentional: true });
      return;
    }

    if (isDuplicateSessionKick(code, reason)) {
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, duplicateSession: true });
      return;
    }

    const maxAttempts = 6;
    if (token && code !== LOBBY_CLOSE_CONSENTED && this._reconnectAttempt < maxAttempts) {
      this._reconnectAttempt += 1;
      this._emitLocal('reconnecting', { code, reason, attempt: this._reconnectAttempt, phase: 'token' });
      const delayMs = Math.min(4000, 200 * this._reconnectAttempt ** 2);
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const newRoom = await this.client.reconnect(token);
        this.room = newRoom;
        this.connected = true;
        this._reconnectAttempt = 0;
        newRoom.onLeave(this._leaveHandler);
        this._wireInbound();
        writePersistedLobbyReconnect(this.joinArgs.mode, newRoom);
        this._emitLocal('reconnected', { code, reason });
        return;
      } catch (e) {
        console.warn('[colyseus-lobby] reconnect failed, trying full join', e);
      }
    }

    this._emitLocal('reconnecting', { code, reason, phase: 'full_join' });
    try {
      const newRoom = await this.client.joinOrCreate('lobby', this._joinOptsForFallback());
      this.room = newRoom;
      this.connected = true;
      this._reconnectAttempt = 0;
      newRoom.onLeave(this._leaveHandler);
      this._wireInbound();
      writePersistedLobbyReconnect(this.joinArgs.mode, newRoom);
      this._emitLocal('reconnected', { code, reason, fallback: 'join' });
    } catch (e) {
      console.error('[colyseus-lobby] full rejoin failed', e);
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, error: e });
    }
  }

  _wireInbound() {
    this.room.onMessage(EVENTS.ERROR, (payload) => {
      this._emitLocal('error', payload || { message: 'Colyseus error' });
    });

    this.room.onMessage(EVENTS.ACK, (payload) => {
      this._emitLocal('ack', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_STATE, (payload) => {
      const tables = Array.isArray(payload?.tables) ? payload.tables.map(toLegacyTable) : [];
      this._emitLocal('lobby_update', { tables });
    });

    this.room.onMessage(EVENTS.TABLE_CREATED, (payload) => {
      this._emitLocal('table_created', { table: toLegacyTable(payload) });
    });

    this.room.onMessage(EVENTS.TABLE_UPDATED, (payload) => {
      this._emitLocal('table_update', { table: toLegacyTable(payload) });
    });

    this.room.onMessage(EVENTS.TABLE_REMOVED, (payload) => {
      this._emitLocal('table_removed', payload || {});
    });

    this.room.onMessage(EVENTS.TABLE_WAITING_CHAT, (payload) => {
      this._emitLocal('table_waiting_chat', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_FRIEND_REQUEST, (payload) => {
      this._emitLocal('lobby_friend_request', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_FRIEND_REQUEST_RESOLVED, (payload) => {
      this._emitLocal('lobby_friend_request_resolved', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_GLOBAL_LOBBY_CHAT, (payload) => {
      this._emitLocal('lobby_global_lobby_chat', payload || {});
    });
    this.room.onMessage(EVENTS.LOBBY_FRIEND_DM_MESSAGE, (payload) => {
      this._emitLocal('lobby_friend_dm_message', payload || {});
    });
    this.room.onMessage(EVENTS.LOBBY_FRIEND_DM_SYNC, (payload) => {
      this._emitLocal('lobby_friend_dm_sync', payload || {});
    });
    this.room.onMessage(EVENTS.SOCIAL_INVITE, (payload) => {
      this._emitLocal('social_invite', payload || {});
    });
  }

  _emitLocal(eventName, payload) {
    const subs = this.listeners.get(eventName);
    if (!subs || subs.size === 0) return;
    subs.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[colyseus-lobby] listener failed', e);
      }
    });
  }

  on(eventName, cb) {
    if (!this.listeners.has(eventName)) this.listeners.set(eventName, new Set());
    this.listeners.get(eventName).add(cb);
  }

  off(eventName, cb) {
    if (!this.listeners.has(eventName)) return;
    if (!cb) {
      this.listeners.delete(eventName);
      return;
    }
    this.listeners.get(eventName).delete(cb);
  }

  emit(eventName, payload) {
    switch (eventName) {
      case 'join_lobby':
      case 'GET_ROOM_LIST':
        this.room.send('command', createEnvelope(COMMANDS.LIST_TABLES, {}));
        return;
      case 'create_table':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.CREATE_TABLE, {
            tableId: payload?.tableId,
            name: payload?.name,
            mode: payload?.mode || 'seka',
            ...(String(payload?.mode || 'seka') === 'belot'
              ? {
                  belotVariant: String(payload?.belotVariant || DEFAULT_BELOT_VARIANT).trim().toLowerCase(),
                  belotTargetScore: Number(payload?.belotTargetScore) === 501 ? 501 : 1001,
                  belotShulerEnabled: Boolean(
                    payload?.belotShulerEnabled &&
                      Number(payload?.maxPlayers) === 4 &&
                      String(payload?.belotVariant || DEFAULT_BELOT_VARIANT).trim().toLowerCase() !==
                        'open_duell',
                  ),
                }
              : {}),
            currency: payload?.currency || 'seka',
            buyIn: payload?.buyIn,
            maxPlayers: payload?.maxPlayers,
            ...(Number.isFinite(Number(payload?.ante)) ? { ante: Number(payload.ante) } : {}),
            ...(payload?.isPrivate
              ? {
                  isPrivate: true,
                  roomPassword: String(payload?.roomPassword ?? payload?.password ?? '').trim(),
                }
              : {}),
          })
        );
        return;
      case 'join_table':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.JOIN_TABLE, {
            tableId: String(payload?.tableId || '')
              .trim()
              .toUpperCase(),
            ...(String(payload?.roomPassword || '').trim()
              ? { roomPassword: String(payload?.roomPassword || '').trim() }
              : {}),
          })
        );
        return;
      case 'leave_table':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.LEAVE_TABLE, {
            tableId: String(payload?.tableId || '')
              .trim()
              .toUpperCase(),
          })
        );
        return;
      case 'set_table_ready':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.SET_READY, {
            tableId: String(payload?.tableId || '')
              .trim()
              .toUpperCase(),
            ready: Boolean(payload?.ready),
          })
        );
        return;
      case 'kick_from_table':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.KICK_FROM_TABLE, {
            tableId: String(payload?.tableId || '')
              .trim()
              .toUpperCase(),
            targetUserId: String(payload?.targetUserId || ''),
          })
        );
        return;
      case 'table_waiting_chat':
        this.room.send(
          'command',
          createEnvelope(COMMANDS.TABLE_WAITING_CHAT, {
            tableId: String(payload?.tableId || '')
              .trim()
              .toUpperCase(),
            text: String(payload?.text || '').trim().slice(0, 280),
          })
        );
        return;
      case 'friend_request_notify': {
        const targetUserId = String(payload?.targetUserId || '').trim();
        if (!targetUserId) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.FRIEND_REQUEST_NOTIFY, {
            targetUserId,
          })
        );
        return;
      }
      case 'friend_request_resolve': {
        const otherUserId = String(payload?.otherUserId || '').trim();
        const status = String(payload?.status || '').toLowerCase();
        if (!otherUserId || (status !== 'accepted' && status !== 'declined')) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_REQUEST_RESOLVE, { otherUserId, status }));
        return;
      }
      case 'GLOBAL_LOBBY_CHAT': {
        const text = String(payload?.text || '').trim().slice(0, 220);
        if (!text) return;
        this.room.send('command', createEnvelope(COMMANDS.GLOBAL_LOBBY_CHAT, { text }));
        return;
      }
      case 'friend_dm_send': {
        const toUserId = String(payload?.toUserId || '').trim();
        const text = String(payload?.text || '').trim().slice(0, 220);
        if (!toUserId || !text) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_DM_SEND, { toUserId, text }));
        return;
      }
      case 'friend_dm_sync':
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_DM_SYNC, {}));
        return;
      case 'friend_dm_mark_read': {
        const peerUserId = String(payload?.peerUserId || '').trim();
        if (!peerUserId) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_DM_MARK_READ, { peerUserId }));
        return;
      }
      // Backward-compat alias for old typo used by stale clients.
      case 'friend_dm_mark_lead': {
        const peerUserId = String(payload?.peerUserId || '').trim();
        if (!peerUserId) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_DM_MARK_READ, { peerUserId }));
        return;
      }
      case 'social_send_invite': {
        const targetUserId = String(payload?.targetUserId || payload?.toUserId || '').trim();
        if (!targetUserId) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.SEND_INVITE, {
            targetUserId,
            mode: String(payload?.mode || 'seka'),
            belotVariant: String(payload?.belotVariant || '').trim().toLowerCase(),
            stake: Math.max(1, Math.floor(Number(payload?.stake) || 0)),
            ante: Math.max(0, Math.floor(Number(payload?.ante) || 0)),
            roomCode: String(payload?.roomCode || payload?.tableId || '').trim().toUpperCase(),
            gameRoomId: String(payload?.gameRoomId || ''),
          })
        );
        return;
      }
      case 'social_resolve_invite': {
        const inviteId = String(payload?.inviteId || '').trim();
        const decision = String(payload?.decision || '').trim().toLowerCase();
        if (!inviteId || !decision) return;
        this.room.send('command', createEnvelope(COMMANDS.RESOLVE_INVITE, { inviteId, decision }));
        return;
      }
      default:
        console.warn(`[colyseus-lobby] unsupported emit event: ${eventName}`);
    }
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this.connected = false;
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* ignore */
    }
    clearPersistedLobbyReconnect(this.joinArgs.mode);
    this.room.leave(true);
    this.listeners.clear();
  }
}

export { ColyseusLobbyTransport };
